'use client';

import { buildAssistantOpenContext } from './pageContext';
import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';

interface AssistantContextType {
  /** The fallback modal surface, used where no shell sidebar is mounted. */
  isOpen: boolean;
  /** The shell sidebar's chat mode. Opening prefers it whenever a host is mounted. */
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /** Called by the shell sidebar; returns its unregister function. */
  registerSidebarHost: () => () => void;
  openAssistant: (context?: Record<string, any>) => void;
  closeAssistant: () => void;
  toggleAssistant: () => void;
  clearAssistantHandoff: () => void;
  assistantContext: Record<string, any>;
}

const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const sidebarHosts = useRef(0);
  const [assistantContext, setAssistantContext] = useState<Record<string, any>>({});
  const handoffRef = useRef(0);

  const openAssistant = (context?: Record<string, any>) => {
    if (context) {
      handoffRef.current += 1;
      setAssistantContext({ ...buildAssistantOpenContext(context), assistantHandoff: handoffRef.current });
    } else {
      setAssistantContext(buildAssistantOpenContext());
    }
    if (sidebarHosts.current > 0) setSidebarOpen(true);
    else setIsOpen(true);
  };

  const registerSidebarHost = useCallback(() => {
    sidebarHosts.current += 1;
    return () => {
      sidebarHosts.current = Math.max(0, sidebarHosts.current - 1);
      if (sidebarHosts.current === 0) setSidebarOpen(false);
    };
  }, []);

  const clearAssistantHandoff = () => {
    setAssistantContext((prev) => {
      if (!prev.initialPrompt && !prev.startNewChat && !prev.assistantHandoff) return prev;
      const { initialPrompt: _p, autoSend: _a, startNewChat: _s, assistantHandoff: _h, ...rest } = prev;
      return rest;
    });
  };

  const closeAssistant = () => { setIsOpen(false); setSidebarOpen(false); };

  const toggleAssistant = () => { if (isOpen || isSidebarOpen) closeAssistant(); else openAssistant(); };

  return (
    <AssistantContext.Provider
      value={{
        isOpen,
        isSidebarOpen,
        setSidebarOpen,
        registerSidebarHost,
        openAssistant,
        closeAssistant,
        toggleAssistant,
        clearAssistantHandoff,
        assistantContext,
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
}

export function useAssistantPopup() {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error('useAssistantPopup must be used within an AssistantProvider');
  }
  return context;
}
